// Site-wide script, loaded on every page. Keep it tiny.
import { apply, load, save, type Settings } from "../lib/settings"
import { durationBucket, flush, pagesBucket, referrerClass, returnBucket, track } from "../lib/analytics"
import type { GameEventDetail } from "../lib/game-events"
import { localISO } from "../lib/daily"
import { countVisitDay, initInstallTip, standalone } from "../lib/install-tip"
import { firstTouch, visitDaysBucket } from "../lib/acquisition"
import { note as noteRecent } from "../lib/continue"
import { initFinish } from "../lib/finish"
import { initShareBars } from "../lib/share"

document.documentElement.classList.add("js")
let settings: Settings = load()
apply(settings)

function update(patch: Partial<Settings>): void {
  settings = { ...settings, ...patch }
  apply(settings)
  save(settings)
  syncButtons()
  document.dispatchEvent(new CustomEvent("dt:settings", { detail: settings }))
  document.dispatchEvent(new Event("dt:relayout")) // text and card sizes change the board's room
  for (const [k, v] of Object.entries(patch)) track("settings_change", { setting: k, value: String(v) })
}

function syncButtons(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-setting]").forEach((b) => {
    const key = b.dataset.setting as keyof Settings
    b.setAttribute("aria-pressed", String(String(settings[key]) === (b.dataset.value ?? "")))
  })
}

// Printables: a changed option redraws the sheet at once (owner, 2026-10-10: "I don't see anything move when changing
// controls"), keeping the same puzzles (the form's hidden seed) and the scroll position. Without JS, the Show
// button still does it, so it is hidden only here.
const printForm = document.querySelector<HTMLFormElement>("form.print-options")
if (printForm) {
  printForm.querySelector<HTMLElement>('button[type="submit"]:not([name])')?.setAttribute("hidden", "")
  printForm.addEventListener("change", (e) => {
    if (!(e.target as HTMLElement).matches("input, select")) return
    try { sessionStorage.setItem("dt:print-scroll", String(window.scrollY)) } catch { /* fine: it just opens at the top */ }
    printForm.requestSubmit()
  })
  try {
    const y = sessionStorage.getItem("dt:print-scroll")
    if (y !== null) { sessionStorage.removeItem("dt:print-scroll"); window.scrollTo(0, Number(y)) }
  } catch { /* storage blocked */ }
}

document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement | null
  if (target?.closest("[data-print]")) { window.print(); return }
  const reveal = target?.closest<HTMLElement>("[data-email-reveal]")
  if (reveal && target?.closest("button")) {
    // ApplicationHelper#email_reveal: the address is stored reversed and Base64-encoded, never as plain text.
    const email = atob(reveal.dataset.emailReveal ?? "").split("").reverse().join("")
    const link = document.createElement("a")
    link.href = `mailto:${email}`
    link.textContent = email
    reveal.replaceChildren(link)
    link.focus()
    return
  }
  // The game Menu (games/_game_menu, phones only): the toggle opens it; choosing anything in it, or a click
  // anywhere else, closes it.
  const gameMenu = document.querySelector<HTMLElement>("[data-game-menu]")
  if (gameMenu) {
    const open = gameMenu.classList.contains("is-open")
    if (target?.closest("[data-game-menu-toggle]")) {
      setGameMenu(!open)
      if (!open) gameMenu.querySelector<HTMLElement>(".game-menu-panel button:not([hidden]):not(:disabled)")?.focus()
      return
    }
    if (open && (!target?.closest(".game-menu-panel") || target.closest("button"))) setGameMenu(false)
  }
  // The Menu (narrow screens): opens over the page, so nothing moves; Escape, a click elsewhere or a link closes it.
  const menuBtn = target?.closest<HTMLButtonElement>("[data-menu-toggle]")
  const menu = document.getElementById("site-menu")
  if (menuBtn && menu) {
    menu.hidden = !menu.hidden
    menuBtn.setAttribute("aria-expanded", String(!menu.hidden))
    if (!menu.hidden) menu.querySelector<HTMLElement>("a")?.focus()
    return
  }
  if (menu && !menu.hidden && !target?.closest("#site-menu")) closeMenu()
  const toggle = target?.closest<HTMLButtonElement>("[data-settings-toggle]")
  if (toggle) {
    const panel = document.getElementById(toggle.getAttribute("aria-controls") ?? "")
    if (panel) {
      panel.hidden = !panel.hidden
      toggle.setAttribute("aria-expanded", String(!panel.hidden))
      document.dispatchEvent(new Event("dt:relayout")) // the panel moves the board up or down
    }
    return
  }
  const btn = target?.closest<HTMLButtonElement>("[data-setting]")
  if (!btn) return
  const raw = btn.dataset.value ?? ""
  const value = raw === "true" ? true : raw === "false" ? false : Number.isNaN(Number(raw)) ? raw : Number(raw)
  update({ [btn.dataset.setting as keyof Settings]: value } as Partial<Settings>)
})
syncButtons()

// ---- measurement (ID-free) ---------------------------------------------------------------------
const board = document.getElementById("board")
const ruleset = board ? `${board.dataset.engine ?? ""}${board.dataset.variant ? `:${board.dataset.variant}` : board.dataset.level ? `:${board.dataset.level}` : ""}` : ""
const isDaily = !!board?.dataset.dailyGame
const pageStart = performance.now()
let started = false
let ended = false

function sessionOnce(): void {
  let first = true
  try { first = !sessionStorage.getItem("dt:session"); sessionStorage.setItem("dt:session", "1") } catch { /* private mode */ }
  if (!first) return
  let storage = "available", hadRecord = false, last: string | null = null, days = 0
  try {
    hadRecord = Object.keys(localStorage).some((k) => k.startsWith("dt:"))
    last = localStorage.getItem("dt:last-visit")
    localStorage.setItem("dt:last-visit", localISO())
    countVisitDay(last, localISO())
    days = Number(localStorage.getItem("dt:visit-days") ?? 0)
  } catch { storage = "throws" }
  const ref = referrerClass(document.referrer, location.href)
  // Return visits by first source: what a paid player is worth depends on how often they come back.
  // dt:last-visit, not "any dt: key": the game saves its deal before this runs on a brand-new browser.
  const { source, campaign } = firstTouch(new URL(location.href), document.referrer, last !== null)
  track("return_bucket", { bucket: returnBucket(last, localISO()), source, ...(campaign ? { campaign } : {}), ...(days ? { visit_days: visitDaysBucket(days) } : {}) })
  const state = { storage, record: hadRecord, possible_loss: !hadRecord && ref === "direct-or-bookmark" }
  void keepStorage(days).then((persisted) => track("storage_state", { ...state, ...(persisted === null ? {} : { persisted }) }))
}

/** Asks the browser to keep this site's saved games, calendar and stats when space runs low (and, as a home-screen
 *  app, through Safari's 7-day clean-up). Chrome and Edge decide quietly from how much the site is used; Firefox
 *  would show a permission prompt, so it is asked only from an installed app, never in a Firefox tab. From the
 *  second visit day, so a one-off visitor isn't asked. Returns whether the data is kept, or null if unknown. */
async function keepStorage(days: number): Promise<boolean | null> {
  const s = navigator.storage
  if (!s?.persisted || !s.persist) return null
  try {
    if (await s.persisted()) return true
    const quiet = /Chrome\/|CriOS\/|Edg\//.test(navigator.userAgent) && !/Firefox\//.test(navigator.userAgent)
    if (days >= 2 && (quiet || standalone())) return await s.persist()
    return false
  } catch { return null }
}

/** Counts this page in the tab's visit (sessionStorage, gone when the tab closes); 0 when storage is blocked. */
function visitPages(): number {
  try {
    const n = Number(sessionStorage.getItem("dt:pages") ?? 0) + 1
    sessionStorage.setItem("dt:pages", String(n))
    return n
  } catch { return 0 }
}

const ready = (): void => {
  const pages = visitPages()
  track("page_view", { ruleset, referrer: referrerClass(document.referrer, location.href), daily: isDaily, ...(pages ? { visit_pages: pagesBucket(pages) } : {}) })
  if (isDaily) track("daily_view", { ruleset })
  sessionOnce()
}
if ("requestIdleCallback" in window) (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(ready)
else setTimeout(ready, 1)

document.addEventListener("click", (e) => {
  const kind = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-reminder]")?.dataset.reminder
  if (kind) track("reminder", { kind })
})

// For "Pick up where you left off" on the home page (lib/continue.ts): this page has a game started, or finished.
const gameName = (): string => document.querySelector("h1")?.textContent?.trim().slice(0, 60) ?? document.title
document.addEventListener("dt:game", (e) => {
  const d = (e as CustomEvent<GameEventDetail>).detail
  if (d.type === "first_move") noteRecent(location.pathname, gameName(), "start")
  else if (d.outcome === "win" || d.outcome === "loss") noteRecent(location.pathname, gameName(), "end")
})

document.addEventListener("dt:game", (e) => {
  const d = (e as CustomEvent<GameEventDetail>).detail
  if (d.type === "first_move") {
    started = true
    ended = false
    track("first_move", { ruleset: d.ruleset, ttfm: durationBucket(performance.now() - pageStart), input: d.input ?? "click", daily: !!d.daily })
  } else {
    ended = d.outcome === "win"
    track("game_end", { ruleset: d.ruleset, outcome: d.outcome ?? "win", moves: d.moves ?? 0, undos: d.undos ?? 0, hints: d.hints ?? 0, deal: String(d.deal ?? ""), daily: !!d.daily, duration: durationBucket(performance.now() - pageStart) })
    if (d.outcome === "new_game") started = false
  }
})
document.addEventListener("dt:daily", (e) => {
  const d = (e as CustomEvent<{ game: string; status: string }>).detail
  if (d.status === "won") track("daily_complete", { ruleset: d.game })
})
addEventListener("beforeprint", () => track("print", { ruleset }))
addEventListener("error", (e) => track("error", { kind: (e.error as Error | undefined)?.name ?? "error" }))
addEventListener("pagehide", () => {
  if (started && !ended) track("game_end", { ruleset, outcome: "abandon", daily: isDaily, duration: durationBucket(performance.now() - pageStart) })
  flush()
})

// ---- installable web app ----------------------------------------------------------------------
// The service worker (PwaController) keeps pages and game files for offline play. Running from the home screen, it
// is asked to fetch every game's files and the coming week's daily puzzles, so all of them work without internet.
if (document.querySelector('meta[name="dt-offline"]') && "serviceWorker" in navigator) {
  addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").then(async () => {
      if (!standalone()) return
      const reg = await navigator.serviceWorker.ready
      reg.active?.postMessage({ type: "warm" })
    }).catch(() => { /* no offline copy: the site still works online */ })
  })
}
initInstallTip()
initFinish()
initShareBars()

// On phones the status line is pinned to the bottom of the screen (application.tailwind.css); it fades a few seconds
// after each new message, or at the next tap anywhere, so a long one never sits over the board. Taps pass through it
// to the buttons underneath (at the largest text it covered the Check and Hint row; bug audit 2026-10-14).
const statusLine = document.querySelector<HTMLElement>("[data-status]")
if (statusLine) {
  let quiet: number | undefined
  new MutationObserver(() => {
    statusLine.classList.remove("is-quiet")
    clearTimeout(quiet)
    quiet = window.setTimeout(() => statusLine.classList.add("is-quiet"), 7000)
  }).observe(statusLine, { childList: true, characterData: true, subtree: true })
  document.addEventListener("pointerdown", () => statusLine.classList.add("is-quiet"), { capture: true })
}

function closeMenu(focusButton = false): void {
  const menu = document.getElementById("site-menu")
  const btn = document.querySelector<HTMLButtonElement>("[data-menu-toggle]")
  if (!menu || menu.hidden) return
  menu.hidden = true
  btn?.setAttribute("aria-expanded", "false")
  if (focusButton) btn?.focus()
}
function setGameMenu(open: boolean, focusButton = false): void {
  const menu = document.querySelector<HTMLElement>("[data-game-menu]")
  if (!menu) return
  menu.classList.toggle("is-open", open)
  // Keep the panel on the screen: it hangs from the More button, which may sit near either edge.
  const panel = menu.querySelector<HTMLElement>(".game-menu-panel")
  if (panel) {
    panel.style.translate = ""
    if (open) {
      const r = panel.getBoundingClientRect()
      const shift = r.left < 8 ? 8 - r.left : r.right > innerWidth - 8 ? innerWidth - 8 - r.right : 0
      if (shift) panel.style.translate = `${shift}px 0`
    }
  }
  const btn = menu.querySelector<HTMLButtonElement>("[data-game-menu-toggle]")
  btn?.setAttribute("aria-expanded", String(open))
  if (focusButton) btn?.focus()
}
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return
  if (document.querySelector("[data-game-menu].is-open")) setGameMenu(false, true)
  else closeMenu(true)
})
